import { memo } from "react";
import { useDispatch } from "react-redux";

import { useChartsContext } from "contexts/ChartContext";
import { defaultWellStatusOverTimeRequest } from "entities/charts/eva/requests/wellStatusOverTime";
import { updateChart } from "store/features/charts/chartSlice";
import { ChartRequestType, WellStatusOverTimeChartRequestType } from "types";

import { useChartEntities } from "hooks/charts";

import { ToolbarButton } from "components/base";
import { DrainLegs as DrainLegsIcon } from "components/icons";

/**
 * Well Status Over Time only — toggles `includeDrainLegs` (EVA-120). `true`
 * counts a multilateral drain leg under its parent well's status via the
 * leg-to-parent-well date join; `false` drops legs from the chart entirely.
 * Not rendered on the Current Well Status chart: that chart keeps the flag
 * pinned `true` (see wellStatusCurrent.ts) and has no toggle for it.
 *
 * Dispatches like the shared TimeScaleSelector/status pickers — a full
 * `updateChart({ request })` write, not `updateChartByPath` — because
 * `includeDrainLegs` lives on the chart's lean request object, not under
 * `features.*`. `chart.request` is `undefined` until the controller's first
 * response lands, so `updateChartByPath`'s `_has()` guard would silently no-op
 * on a toggle click before that.
 */
function DrainLegsToggle() {
  const dispatch = useDispatch();
  const { id } = useChartsContext();
  const { request } = useChartEntities(id);

  // Lean request rides the entity's ChartRequestType-typed field — same cast
  // pattern as the controller hook and TimeScaleSelector.
  const selections = request as unknown as Maybe<
    Partial<WellStatusOverTimeChartRequestType>
  >;
  const isActive =
    selections?.includeDrainLegs ?? defaultWellStatusOverTimeRequest().includeDrainLegs;

  const toggle = (active: boolean) => {
    dispatch(
      updateChart({
        id,
        // Carry the persisted request forward ONLY when it is already this
        // chart's shape.
        //
        // `chart.request` is shared between the two well-status charts and
        // survives a chart-type switch, so between a Current -> Over-Time
        // switch and the controller's next echo it still holds the
        // current-status shape. Spreading that unconditionally re-persists the
        // other chart's `allStatuses` and its `"06. PROD"`-style status onto
        // the entity.
        //
        // Most readers rebuild per the ACTIVE chart and would have discarded
        // it — `useWellStatusController` re-resolves `status` and recomputes
        // `allStatuses`, `WellStatusDataTable` force-overrides
        // `chartType`/`status`/`allStatuses` on export — but
        // `useWellStatusChartOptions` does NOT: it reads
        // `currentRequest?.allStatuses === true` with no `isCurrent` gate, so a
        // leaked `allStatuses: true` titles the over-time value axis
        // "All Statuses" instead of the status the user has selected.
        //
        // Gating on `chartType` is enough and keeps the reason this uses
        // `updateChart` rather than `updateChartByPath` intact: when `request`
        // is `undefined` (before the first response) we still write a complete
        // default-backed object, so an early click is not swallowed.
        request: {
          ...defaultWellStatusOverTimeRequest(),
          ...(selections?.chartType === "WellStatusOverTime" ? selections : undefined),
          includeDrainLegs: active
        } as unknown as ChartRequestType
      })
    );
  };

  return (
    <ToolbarButton
      active={isActive}
      icon={<DrainLegsIcon size={21} />}
      overflowLabel="Drain Legs"
      tooltipText="Include Drain Legs"
      data-testid={isActive ? "drainLegsToggleActive" : "drainLegsToggleInactive"}
      onToggle={toggle}
    />
  );
}

export default memo(DrainLegsToggle);
